import { useEffect, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface RetryImageProps {
  id: string;
  src: string;
  defaultSrc: string;
  alt?: string;
  retryInterval?: number;
  maxRetries?: number;
  className?: string;
  // DnD props
  sortable?: boolean;
}

const DynamicImage = ({
  id,
  src,
  defaultSrc,
  className,
  alt = '',
  retryInterval = 500,
  maxRetries = 20,
  sortable = false,
}: RetryImageProps) => {
  const [currentSrc, setCurrentSrc] = useState<string>(defaultSrc);
  const [attempts, setAttempts] = useState<number>(0);

  // DnD sortable functionality
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !sortable,
  });

  const style = sortable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
      }
    : {};

  useEffect(() => {
    let retryTimeout: NodeJS.Timeout;
    if (attempts < maxRetries) {
      const img = new Image();
      img.src = `${src}?t=${Date.now()}`; // Cache busting
      img.onload = () => setCurrentSrc(src);
      img.onerror = () => {
        retryTimeout = setTimeout(() => setAttempts((prev) => prev + 1), retryInterval);
      };
    }
    return () => clearTimeout(retryTimeout);
  }, [attempts, src, retryInterval, maxRetries]);

  const imageElement = (
    <img
      id={id}
      src={currentSrc}
      alt={alt}
      className={className}
      // Prevent iOS long-press preview/context menu when sortable
      onContextMenu={sortable ? (e) => e.preventDefault() : undefined}
      style={sortable ? { pointerEvents: 'none' } : undefined}
    />
  );

  if (sortable) {
    return (
      <div
        ref={setNodeRef}
        style={{
          ...style,
          touchAction: 'none', // Prevents browser touch handling
          WebkitTouchCallout: 'none', // Disables iOS long-press preview
          WebkitUserSelect: 'none', // Prevents selection
          userSelect: 'none',
        }}
        {...attributes}
        {...listeners}
        className={`${isDragging ? 'opacity-50' : ''} cursor-move`}
      >
        {imageElement}
      </div>
    );
  }

  return imageElement;
};

export default DynamicImage;
